// apps/client/src/components/AIChat/QueryUsageIndicator.tsx

import React, { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import { Zap } from 'lucide-react';

interface QueryAnalytics {
  totalQueries: number;
  queriesThisHour: number;
  queriesRemainingThisHour: number;
  maxQueriesPerHour: number;
  subscriptionTier: string;
}

interface QueryUsageIndicatorProps {
  /** Trigger refresh when this changes (e.g., after sending a message) */
  refreshTrigger?: number;
}

/**
 * Visual query usage indicator similar to Claude's interface
 * Shows remaining queries with a progress bar based on subscription tier
 */
export function QueryUsageIndicator({
  refreshTrigger = 0,
}: QueryUsageIndicatorProps) {
  const [analytics, setAnalytics] = useState<QueryAnalytics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const getAnalytics = httpsCallable<{ timeRange: string }, QueryAnalytics>(
          functions,
          'getAIQueryAnalytics'
        );
        const result = await getAnalytics({ timeRange: 'day' });
        setAnalytics(result.data);
      } catch (error) {
        console.error('Failed to fetch query analytics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [refreshTrigger]);

  if (loading || !analytics) {
    return (
      <div className="flex items-center gap-2 text-gray-400 text-sm animate-pulse">
        <Zap className="w-4 h-4" />
        <span>Loading usage...</span>
      </div>
    );
  }

  const used = analytics.queriesThisHour;
  const remaining = analytics.queriesRemainingThisHour;
  const maxQueriesPerHour = analytics.maxQueriesPerHour || 20;
  const percentUsed = (used / maxQueriesPerHour) * 100;

  // Color based on usage level - high contrast for readability
  const getBarColor = () => {
    if (percentUsed >= 90) return 'bg-red-500';
    if (percentUsed >= 70) return 'bg-amber-500';
    return 'bg-green-500';
  };

  const getTextColor = () => {
    if (percentUsed >= 90) return 'text-red-300';
    if (percentUsed >= 70) return 'text-amber-300';
    return 'text-green-300';
  };

  const getBgColor = () => {
    if (percentUsed >= 90) return 'bg-red-500/20';
    if (percentUsed >= 70) return 'bg-amber-500/20';
    return 'bg-green-500/20';
  };

  return (
    <div className="flex items-center gap-3">
      {/* Icon with background pill */}
      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${getBgColor()}`}>
        <Zap className={`w-4 h-4 ${getTextColor()}`} />

        {/* Count - large and bold */}
        <span className={`text-base font-bold ${getTextColor()}`}>
          {remaining}
        </span>
        <span className="text-gray-400 text-sm">/</span>
        <span className="text-gray-400 text-sm">{maxQueriesPerHour}</span>
      </div>

      {/* Progress bar */}
      <div className="hidden sm:flex items-center gap-2">
        <div className="w-20 h-2 bg-gray-600 rounded-full overflow-hidden">
          <div
            className={`h-full ${getBarColor()} transition-all duration-300`}
            style={{ width: `${Math.min(percentUsed, 100)}%` }}
          />
        </div>
      </div>

      {/* Label */}
      <span className="text-xs text-gray-400 hidden md:inline">
        queries left
      </span>
    </div>
  );
}
